import {
  addDoc,
  arrayRemove,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where
} from "firebase/firestore";
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebaseConfig";
import { countries } from "../../utils/countryData";
import {
  getValidationErrorMessage,
  sanitizeInput,
  validateEmail,
  validateInstagramHandle,
  validatePhoneNumber
} from "../../utils/inputValidation";
import { validateInvitationCode } from "../../utils/invitationCodeGenerator";
import { checkRateLimit } from "../../utils/rateLimiter";
import { isSuspiciousActivity } from "../../utils/suspiciousActivityDetector";
import CountrySelect from "./CountrySelect";

interface RegistrationFormProps {
  eventId: string;
  title: string;
  maxTickets: number;
  currentRegistrations: number;
  isClosedEvent: boolean;
  onRegistrationChange: () => void;
}

// Define the registration data type with all possible fields
interface RegistrationData {
  name: string;
  email: string;
  phoneNumber: string;
  instagram: string;
  friendEmail: string;
  timestamp: Date;
  userId: string | undefined;
  invitedBy: string | null;
  invitationCode?: string; // Make this optional since it's only used for closed events
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  eventId,
  title,
  maxTickets,
  currentRegistrations,
  isClosedEvent,
  onRegistrationChange,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+352"); // Default to LU
  const [phoneNumber, setPhoneNumber] = useState("");
  const [instagram, setInstagram] = useState("");
  const [friendEmail, setFriendEmail] = useState("");
  const [invitationCode, setInvitationCode] = useState("");
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [invitedBy, setInvitedBy] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();

  const isEventFull = currentRegistrations >= maxTickets;

  const checkRegistration = useCallback(async () => {
    if (!user) return;
    const q = query(
      collection(db, `events/${eventId}/registrations`),
      where("userId", "==", user.uid)
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      setAlreadyRegistered(true);
      const userData = querySnapshot.docs[0].data();
      setName(userData.name || "");

      // Split the full phone number into country code and number
      const fullPhoneNumber = userData.phoneNumber || "";
      const matchedCountry = countries.find((country) =>
        fullPhoneNumber.startsWith(country.code)
      );

      if (matchedCountry) {
        setCountryCode(matchedCountry.code);
        setPhoneNumber(fullPhoneNumber.slice(matchedCountry.code.length));
      } else {
        // If no match found, set default values or handle error
        setCountryCode("+1");
        setPhoneNumber(fullPhoneNumber);
      }

      setInstagram(userData.instagram || "");
      setFriendEmail(userData.friendEmail || "");
      setInvitedBy(userData.invitedBy || null);
      
      if (userData.invitationCode) {
        setInvitationCode(userData.invitationCode);
      }
    } else {
      // Check if the user was invited
      const invitationQuery = query(
        collection(db, `events/${eventId}/registrations`),
        where("friendEmail", "==", user.email)
      );
      const invitationSnapshot = await getDocs(invitationQuery);
      if (!invitationSnapshot.empty) {
        const inviterData = invitationSnapshot.docs[0].data();
        setInvitedBy(inviterData.name);
      }
    }
  }, [user, eventId]);

  useEffect(() => {
    if (user) {
      setEmail(user.email || "");
      checkRegistration();
    }
  }, [user, eventId, checkRegistration]);

  const validateAndCheckInvitationCode = async (code: string): Promise<boolean> => {
    // First validate the format
    if (!validateInvitationCode(code)) {
      setError("The invitation code format is invalid.");
      return false;
    }
    
    try {
      // Check if the code exists in the availableCodes array
      const codesDoc = await getDoc(doc(db, `events/${eventId}/metadata/invitationCodes`));
      
      if (!codesDoc.exists()) {
        setError("Invitation codes not found for this event.");
        return false;
      }
      
      const codesData = codesDoc.data();
      const availableCodes = codesData.availableCodes || [];
      
      if (!availableCodes.includes(code)) {
        setError("Invalid invitation code. Please check and try again.");
        return false;
      }
      
      return true;
    } catch (error) {
      console.error("Error validating invitation code:", error);
      setError("An error occurred while validating your invitation code.");
      return false;
    }
  };
  
  const removeUsedInvitationCode = async (code: string): Promise<void> => {
    try {
      const codesDocRef = doc(db, `events/${eventId}/metadata/invitationCodes`);
      
      // Remove the code from availableCodes array
      await updateDoc(codesDocRef, {
        availableCodes: arrayRemove(code)
      });
    } catch (error) {
      console.error("Error removing used invitation code:", error);
      // We don't need to throw here, as the registration was already successful
      // Just log the error for debugging
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    if (alreadyRegistered || isEventFull) return;
    if (isSubmitting) return;

    setIsSubmitting(true);

    // Rate limiting check
    if (!checkRateLimit(user?.uid || "anonymous", 5, 60000)) {
      setError("Too many registration attempts. Please try again later.");
      setIsSubmitting(false);
      return;
    }

    const sanitizedName = sanitizeInput(name);
    const sanitizedPhoneNumber = sanitizeInput(phoneNumber);
    const sanitizedInstagram = sanitizeInput(instagram);
    const sanitizedFriendEmail = sanitizeInput(friendEmail);
    const sanitizedInvitationCode = sanitizeInput(invitationCode).toUpperCase();

    const fullPhoneNumber = `${countryCode}${sanitizedPhoneNumber}`;

    // Validations
    if (!sanitizedName.trim()) {
      setError(getValidationErrorMessage("name"));
      setIsSubmitting(false);
      return;
    }

    if (!validatePhoneNumber(fullPhoneNumber)) {
      setError(getValidationErrorMessage("phoneNumber"));
      setIsSubmitting(false);
      return;
    }

    if (sanitizedInstagram && !validateInstagramHandle(sanitizedInstagram)) {
      setError(getValidationErrorMessage("instagramHandle"));
      setIsSubmitting(false);
      return;
    }

    if (sanitizedFriendEmail && !validateEmail(sanitizedFriendEmail)) {
      setError(getValidationErrorMessage("email"));
      setIsSubmitting(false);
      return;
    }

    // Invitation code validation for closed events
    if (isClosedEvent) {
      if (!sanitizedInvitationCode) {
        setError("Invitation code is required for this event.");
        setIsSubmitting(false);
        return;
      }
      
      const isCodeValid = await validateAndCheckInvitationCode(sanitizedInvitationCode);
      if (!isCodeValid) {
        setIsSubmitting(false);
        return;
      }
    }

    // Suspicious activity check
    const suspiciousActivityResult = isSuspiciousActivity({
      email,
      name: sanitizedName,
      phoneNumber: fullPhoneNumber,
      instagram: sanitizedInstagram,
    });

    if (suspiciousActivityResult.isSuspicious) {
      setError(
        `Registration flagged as suspicious: ${suspiciousActivityResult.reason}`
      );
      setIsSubmitting(false);
      return;
    }

    try {
      // Check if friend is already registered
      if (sanitizedFriendEmail) {
        const friendQuery = query(
          collection(db, `events/${eventId}/registrations`),
          where("email", "==", sanitizedFriendEmail)
        );
        const friendSnapshot = await getDocs(friendQuery);
        if (!friendSnapshot.empty) {
          setError("Your friend is already registered for this event.");
          setIsSubmitting(false);
          return;
        }
      }

      // Check if the event is full before submitting
      if (currentRegistrations >= maxTickets) {
        setError(
          "Sorry, the event is now full. No more registrations can be accepted."
        );
        setIsSubmitting(false);
        return;
      }

      // Prepare registration data with proper typing
      const registrationData: RegistrationData = {
        name: sanitizedName,
        email,
        phoneNumber: fullPhoneNumber,
        instagram: sanitizedInstagram,
        friendEmail: sanitizedFriendEmail,
        timestamp: new Date(),
        userId: user?.uid,
        invitedBy: invitedBy,
      };

      // Add invitation code for closed events
      if (isClosedEvent && sanitizedInvitationCode) {
        registrationData.invitationCode = sanitizedInvitationCode;
      }

      // Add registration document
      await addDoc(collection(db, `events/${eventId}/registrations`), registrationData);
      
      // If closed event, remove the used invitation code
      if (isClosedEvent && sanitizedInvitationCode) {
        await removeUsedInvitationCode(sanitizedInvitationCode);
      }
      
      setAlreadyRegistered(true);
      alert(`Thank you for registering for ${title}, ${sanitizedName}!`);
      onRegistrationChange();
    } catch (error) {
      console.error("Error adding document: ", error);
      setError("An error occurred while registering. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-secondary text-text p-6">
        <h2 className="text-2xl font-bold">Register</h2>
        
      </div>
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {error && <p className="text-red-500">{error}</p>}
        {invitedBy && (
          <p className="text-green-600">You were invited by {invitedBy}!</p>
        )}
        {isEventFull && !alreadyRegistered && (
          <p className="text-red-500">
            Sorry, this event is now full. No more registrations can be
            accepted.
          </p>
        )}
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            Name
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={alreadyRegistered || isEventFull}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
          />
        </div>
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700"
          >
            Email
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={true}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
          />
        </div>
        <div className="flex space-x-2 items-end">
          <div className="w-1/3 flex flex-col justify-end">
            <label
              htmlFor="countryCode"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Country Code
            </label>
            <CountrySelect
              value={countryCode}
              onChange={setCountryCode}
              disabled={alreadyRegistered || isEventFull}
            />
          </div>
          <div className="w-2/3 flex flex-col justify-end">
            <label
              htmlFor="phoneNumber"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Phone Number
            </label>
            <input
              type="tel"
              id="phoneNumber"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              required
              disabled={alreadyRegistered || isEventFull}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
            />
          </div>
        </div>
        <div>
          <label
            htmlFor="instagram"
            className="block text-sm font-medium text-gray-700"
          >
            Instagram Handle
          </label>
          <input
            type="text"
            id="instagram"
            value={instagram}
            onChange={(e) => setInstagram(e.target.value)}
            disabled={alreadyRegistered || isEventFull}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
          />
        </div>
        
        {/* Invitation code field (only shown for closed events) */}
        {isClosedEvent && (
          <div>
            <label
              htmlFor="invitationCode"
              className="block text-sm font-medium text-gray-700"
            >
              Invitation Code <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              id="invitationCode"
              value={invitationCode}
              onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
              required
              disabled={alreadyRegistered || isEventFull}
              placeholder="Enter your invitation code"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50 uppercase"
            />
            <p className="mt-1 text-xs text-gray-500">
              Required to register for this private event
            </p>
          </div>
        )}
        
        <div>
          <label
            htmlFor="friendEmail"
            className="block text-sm font-medium text-gray-700"
          >
            Invite a Friend (Optional)
          </label>
          <input
            type="email"
            id="friendEmail"
            value={friendEmail}
            onChange={(e) => setFriendEmail(e.target.value)}
            disabled={alreadyRegistered || isEventFull}
            placeholder="Friend's email address"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
          />
        </div>
        <button
          type="submit"
          disabled={alreadyRegistered || isEventFull || isSubmitting}
          className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
            alreadyRegistered || isEventFull || isSubmitting
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-primary hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          }`}
        >
          {isSubmitting 
            ? "Registering..." 
            : alreadyRegistered
              ? "Already Registered"
              : isEventFull
                ? "Event Full"
                : "Register"}
        </button>
      </form>
      {alreadyRegistered && (
        <div className="bg-green-100 text-green-700 p-4 text-center">
          You're all set! We look forward to seeing you at the event.
        </div>
      )}
    </div>
  );
};

export default RegistrationForm;